import type {
  JurisdictionResponseSchema,
  JurisdictionWithLicenseTypeConfigurationResponseSchema,
  LicenseTypeConfigurationResponseSchema,
  OrdinanceVersionResponseSchema,
} from "@/hera-api/client/types.gen";
import { coerceLtcBooleanLike } from "@/lib/ltc-hera-remittance-ui";

/**
 * One selectable row: the in-force license configuration (`kind` "live", labeled Current)
 * or a historic ordinance snapshot.
 */
export type OrdinanceListRow = {
  rowKey: string;
  kind: "live" | "historic";
  jurisdictionId: string;
  jurisdictionName: string;
  county: string | null | undefined;
  state: string | null | undefined;
  code: string;
  fips_code: string;
  display_code: string;
  licenseTypeId: string;
  licenseTypeName: string;
  configurationId: string;
  effectiveStartDate: string;
  active: "Yes" | "No";
  registration_fee: string | null | undefined;
  registration_fee_2?: string | null | undefined;
  registration_fee_3?: string | null | undefined;
  registration_fee_4?: string | null | undefined;
  registration_fee_5?: string | null | undefined;
  registration_fee_6?: string | null | undefined;
  registration_fee_7?: string | null | undefined;
  registration_fee_8?: string | null | undefined;
  registration_fee_9?: string | null | undefined;
  registration_fee_10?: string | null | undefined;
  registration_fee_11?: string | null | undefined;
  registration_fee_12?: string | null | undefined;
  inspection_fee?: string | null | undefined;
  inspection_fee_2?: string | null | undefined;
  inspection_fee_3?: string | null | undefined;
  inspection_fee_4?: string | null | undefined;
  inspection_fee_5?: string | null | undefined;
  inspection_fee_6?: string | null | undefined;
  inspection_fee_7?: string | null | undefined;
  inspection_fee_8?: string | null | undefined;
  inspection_fee_9?: string | null | undefined;
  inspection_fee_10?: string | null | undefined;
  inspection_fee_11?: string | null | undefined;
  inspection_fee_12?: string | null | undefined;
  inspection_fee_structure?: string | null | undefined;
  inspection_fee_renewal_type?: string | null | undefined;
  inspection_fee_renewal_period_months?: string | null | undefined;
  inspection_fee_fixed_renewal_day?: string | null | undefined;
  inspection_fee_fixed_renewal_month?: string | null | undefined;
  fee_structure: string | null | undefined;
  renewal_type: string | null | undefined;
  renewal_period_months?: string | null | undefined;
  fixed_renewal_day?: string | null | undefined;
  fixed_renewal_month?: string | null | undefined;
  first_late_fee_enabled?: string | null | undefined;
  first_late_fee_amount?: string | null | undefined;
  first_late_fee_percentage?: string | null | undefined;
  first_late_fee_days?: string | null | undefined;
  recurring_late_fee_enabled?: string | null | undefined;
  recurring_late_fee_amount?: string | null | undefined;
  recurring_late_fee_percentage?: string | null | undefined;
  recurring_late_fee_days?: string | null | undefined;
  late_fee_cap?: string | null | undefined;
  ordinance?: string | null | undefined;
  omt?: string | null | undefined;
  omt_period?: string | null | undefined;
  hera_reg_amount?: string | null | undefined;
  hera_reg_percentage?: string | null | undefined;
  county_reg_amount?: string | null | undefined;
  county_reg_percentage?: string | null | undefined;
  hera_late_amount?: string | null | undefined;
  hera_late_percentage?: string | null | undefined;
  hera_inspection_amount?: string | null | undefined;
  hera_inspection_percentage?: string | null | undefined;
  deregistration_fee_type?: string | null | undefined;
  deregistration_fee?: string | null | undefined;
  deregistration_initial_fee?: string | null | undefined;
  deregistration_subsequent_fee?: string | null | undefined;
  hera_deregistration_amount?: string | null | undefined;
  hera_deregistration_percentage?: string | null | undefined;
  hera_retain_remittance?: string | null | undefined;
  auto_registration_approval?: string | null | undefined;
  auto_deregistration_approval?: string | null | undefined;
  auto_prefiling_deregistration_approval?: string | null | undefined;
  /** Parent configuration (for prefills & live rule display). */
  apiConfig: LicenseTypeConfigurationResponseSchema;
  /** When kind is historic, the backing API row. */
  historicVersion: OrdinanceVersionResponseSchema | null;
};

const REGISTRATION_FEE_FIELDS = [
  "registration_fee",
  "registration_fee_2",
  "registration_fee_3",
  "registration_fee_4",
  "registration_fee_5",
  "registration_fee_6",
  "registration_fee_7",
  "registration_fee_8",
  "registration_fee_9",
  "registration_fee_10",
  "registration_fee_11",
  "registration_fee_12",
] as const satisfies readonly (keyof OrdinanceListRow)[];

/** Lowest configured registration fee on the row, including later tiers. */
export function minimumLicenseFee(
  row: Pick<OrdinanceListRow, (typeof REGISTRATION_FEE_FIELDS)[number]>,
): string | null {
  let best: { numeric: number; raw: string } | null = null;
  for (const field of REGISTRATION_FEE_FIELDS) {
    const raw = row[field];
    if (raw == null) continue;
    const text = String(raw).trim();
    if (text === "") continue;
    const numeric = Number(text);
    if (!Number.isFinite(numeric)) continue;
    if (best == null || numeric < best.numeric) {
      best = { numeric, raw: text };
    }
  }
  return best?.raw ?? null;
}

function rowFromSource(
  source:
    | LicenseTypeConfigurationResponseSchema
    | OrdinanceVersionResponseSchema,
): Pick<
  OrdinanceListRow,
  | "registration_fee"
  | "registration_fee_2"
  | "registration_fee_3"
  | "registration_fee_4"
  | "registration_fee_5"
  | "registration_fee_6"
  | "registration_fee_7"
  | "registration_fee_8"
  | "registration_fee_9"
  | "registration_fee_10"
  | "registration_fee_11"
  | "registration_fee_12"
  | "inspection_fee"
  | "inspection_fee_2"
  | "inspection_fee_3"
  | "inspection_fee_4"
  | "inspection_fee_5"
  | "inspection_fee_6"
  | "inspection_fee_7"
  | "inspection_fee_8"
  | "inspection_fee_9"
  | "inspection_fee_10"
  | "inspection_fee_11"
  | "inspection_fee_12"
  | "inspection_fee_structure"
  | "inspection_fee_renewal_type"
  | "inspection_fee_renewal_period_months"
  | "inspection_fee_fixed_renewal_day"
  | "inspection_fee_fixed_renewal_month"
  | "fee_structure"
  | "renewal_type"
  | "renewal_period_months"
  | "fixed_renewal_day"
  | "fixed_renewal_month"
  | "first_late_fee_enabled"
  | "first_late_fee_amount"
  | "first_late_fee_percentage"
  | "first_late_fee_days"
  | "recurring_late_fee_enabled"
  | "recurring_late_fee_amount"
  | "recurring_late_fee_percentage"
  | "recurring_late_fee_days"
  | "late_fee_cap"
  | "ordinance"
  | "omt"
  | "omt_period"
  | "hera_reg_amount"
  | "hera_reg_percentage"
  | "county_reg_amount"
  | "county_reg_percentage"
  | "hera_late_amount"
  | "hera_late_percentage"
  | "hera_inspection_amount"
  | "hera_inspection_percentage"
  | "deregistration_fee_type"
  | "deregistration_fee"
  | "deregistration_initial_fee"
  | "deregistration_subsequent_fee"
  | "hera_deregistration_amount"
  | "hera_deregistration_percentage"
  | "hera_retain_remittance"
  | "auto_registration_approval"
  | "auto_deregistration_approval"
  | "auto_prefiling_deregistration_approval"
> {
  const firstLateEnabled = coerceLtcBooleanLike(source.first_late_fee_enabled);
  const recurringLateEnabled = coerceLtcBooleanLike(
    source.recurring_late_fee_enabled,
  );
  return {
    registration_fee: source.registration_fee,
    registration_fee_2: source.registration_fee_2,
    registration_fee_3: source.registration_fee_3,
    registration_fee_4: source.registration_fee_4,
    registration_fee_5: source.registration_fee_5,
    registration_fee_6: source.registration_fee_6,
    registration_fee_7: source.registration_fee_7,
    registration_fee_8: source.registration_fee_8,
    registration_fee_9: source.registration_fee_9,
    registration_fee_10: source.registration_fee_10,
    registration_fee_11: source.registration_fee_11,
    registration_fee_12: source.registration_fee_12,
    inspection_fee: source.inspection_fee,
    inspection_fee_2: source.inspection_fee_2,
    inspection_fee_3: source.inspection_fee_3,
    inspection_fee_4: source.inspection_fee_4,
    inspection_fee_5: source.inspection_fee_5,
    inspection_fee_6: source.inspection_fee_6,
    inspection_fee_7: source.inspection_fee_7,
    inspection_fee_8: source.inspection_fee_8,
    inspection_fee_9: source.inspection_fee_9,
    inspection_fee_10: source.inspection_fee_10,
    inspection_fee_11: source.inspection_fee_11,
    inspection_fee_12: source.inspection_fee_12,
    inspection_fee_structure: source.inspection_fee_structure,
    inspection_fee_renewal_type: source.inspection_fee_renewal_type,
    inspection_fee_renewal_period_months:
      source.inspection_fee_renewal_period_months?.toString() ?? null,
    inspection_fee_fixed_renewal_day:
      source.inspection_fee_fixed_renewal_day?.toString() ?? null,
    inspection_fee_fixed_renewal_month:
      source.inspection_fee_fixed_renewal_month?.toString() ?? null,
    fee_structure: source.fee_structure,
    renewal_type: source.renewal_type,
    renewal_period_months: source.renewal_period_months?.toString() ?? null,
    fixed_renewal_day: source.fixed_renewal_day?.toString() ?? null,
    fixed_renewal_month: source.fixed_renewal_month?.toString() ?? null,
    first_late_fee_enabled: firstLateEnabled ? "Yes" : "No",
    first_late_fee_amount: firstLateEnabled
      ? source.first_late_fee_amount
      : null,
    first_late_fee_percentage: firstLateEnabled
      ? source.first_late_fee_percentage
      : null,
    first_late_fee_days: firstLateEnabled
      ? (source.first_late_fee_days?.toString() ?? null)
      : null,
    recurring_late_fee_enabled: recurringLateEnabled ? "Yes" : "No",
    recurring_late_fee_amount: recurringLateEnabled
      ? source.recurring_late_fee_amount
      : null,
    recurring_late_fee_percentage: recurringLateEnabled
      ? source.recurring_late_fee_percentage
      : null,
    recurring_late_fee_days: recurringLateEnabled
      ? (source.recurring_late_fee_days?.toString() ?? null)
      : null,
    late_fee_cap: source.late_fee_cap,
    ordinance: source.ordinance,
    omt: source.omt,
    omt_period: source.omt_period?.toString() ?? null,
    hera_reg_amount: source.hera_reg_amount,
    hera_reg_percentage: source.hera_reg_percentage,
    county_reg_amount: source.county_reg_amount,
    county_reg_percentage: source.county_reg_percentage,
    hera_late_amount: source.hera_late_amount,
    hera_late_percentage: source.hera_late_percentage,
    hera_inspection_amount: source.hera_inspection_amount,
    hera_inspection_percentage: source.hera_inspection_percentage,
    deregistration_fee_type: source.deregistration_fee_type,
    deregistration_fee: source.deregistration_fee,
    deregistration_initial_fee: source.deregistration_initial_fee,
    deregistration_subsequent_fee: source.deregistration_subsequent_fee,
    hera_deregistration_amount: source.hera_deregistration_amount,
    hera_deregistration_percentage: source.hera_deregistration_percentage,
    hera_retain_remittance: source.hera_retain_remittance ? "Yes" : "No",
    auto_registration_approval: source.auto_registration_approval
      ? "Yes"
      : "No",
    auto_deregistration_approval: source.auto_deregistration_approval
      ? "Yes"
      : "No",
    auto_prefiling_deregistration_approval:
      source.auto_prefiling_deregistration_approval ? "Yes" : "No",
  };
}

function sortedVersions(
  cfg: LicenseTypeConfigurationResponseSchema,
): OrdinanceVersionResponseSchema[] {
  const list = cfg.ordinance_versions ?? [];
  return [...list].sort(
    (a, b) =>
      b.effective_start_date.localeCompare(a.effective_start_date) ||
      a.id.localeCompare(b.id),
  );
}

type OrdinanceRowJurisdictionContext = Pick<
  OrdinanceListRow,
  | "jurisdictionId"
  | "jurisdictionName"
  | "county"
  | "state"
  | "code"
  | "fips_code"
  | "display_code"
  | "licenseTypeId"
  | "licenseTypeName"
  | "configurationId"
  | "apiConfig"
>;

function pushHistoricRows(
  rows: OrdinanceListRow[],
  base: OrdinanceRowJurisdictionContext,
  cfg: LicenseTypeConfigurationResponseSchema,
) {
  const currentStart = cfg.ordinance_start_date;
  for (const v of sortedVersions(cfg)) {
    // The row in force today is always the live (current) row from apiConfig; do not duplicate
    // that period as a historic snapshot when a version shares the same effective date.
    if (v.effective_start_date === currentStart) {
      continue;
    }
    rows.push({
      ...base,
      rowKey: `historic:${v.id}`,
      kind: "historic",
      effectiveStartDate: v.effective_start_date,
      active: (v as { is_deleted?: boolean }).is_deleted ? "No" : "Yes",
      ...rowFromSource(v),
      historicVersion: v,
    });
  }
}

/** Operator: flatten GET /operator/jurisdictions payload. */
export function buildOrdinanceRowsFromOperatorJurisdictions(
  jurisdictions: JurisdictionWithLicenseTypeConfigurationResponseSchema[],
): OrdinanceListRow[] {
  const rows: OrdinanceListRow[] = [];
  const sortedJurs = [...jurisdictions].sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  for (const j of sortedJurs) {
    const configs = [...(j.license_type_configurations ?? [])]
      .filter((c) => !c.deleted_at)
      .sort((a, b) =>
        (a.license_type_name ?? "").localeCompare(b.license_type_name ?? ""),
      );
    for (const cfg of configs) {
      const base: OrdinanceRowJurisdictionContext = {
        jurisdictionId: j.id,
        jurisdictionName: j.name,
        county: j.county,
        state: j.state,
        code: j.code,
        fips_code: j.fips_code,
        display_code: j.display_code,
        licenseTypeId: cfg.license_type_id,
        licenseTypeName: cfg.license_type_name,
        configurationId: cfg.id,
        apiConfig: cfg,
      };

      // Always list the in-force configuration; historic rows are additional snapshots only.
      rows.push({
        ...base,
        rowKey: `live:${cfg.id}`,
        kind: "live",
        effectiveStartDate: cfg.ordinance_start_date,
        active: "Yes",
        ...rowFromSource(cfg),
        historicVersion: null,
      });
      pushHistoricRows(rows, base, cfg);
    }
  }
  return rows;
}

/** Government: single jurisdiction + GET license type configurations. */
export function buildOrdinanceRowsFromGovernmentData(
  jurisdiction: JurisdictionResponseSchema,
  configs: LicenseTypeConfigurationResponseSchema[],
): OrdinanceListRow[] {
  const rows: OrdinanceListRow[] = [];
  const sorted = [...configs]
    .filter((c) => !c.deleted_at)
    .sort((a, b) =>
      (a.license_type_name ?? "").localeCompare(b.license_type_name ?? ""),
    );
  for (const cfg of sorted) {
    const base: OrdinanceRowJurisdictionContext = {
      jurisdictionId: jurisdiction.id,
      jurisdictionName: jurisdiction.name,
      county: jurisdiction.county,
      state: jurisdiction.state,
      code: jurisdiction.code,
      fips_code: jurisdiction.fips_code,
      display_code: jurisdiction.display_code,
      licenseTypeId: cfg.license_type_id,
      licenseTypeName: cfg.license_type_name,
      configurationId: cfg.id,
      apiConfig: cfg,
    };

    rows.push({
      ...base,
      rowKey: `live:${cfg.id}`,
      kind: "live",
      effectiveStartDate: cfg.ordinance_start_date,
      active: "Yes",
      ...rowFromSource(cfg),
      historicVersion: null,
    });
    pushHistoricRows(rows, base, cfg);
  }
  return rows;
}
